// Leitor de pulseira (5.5): câmera (BarcodeDetector nativo ou @zxing/browser), leitor físico
// USB/Bluetooth (teclado) e digitação manual. Após cada leitura a câmera é PAUSADA e só volta
// quando o resultado some (alguns segundos) ou quando a tela chama `reset()`.
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent
} from "react";
import { AlertTriangle, Camera, CameraOff, CheckCircle2, Info, Keyboard, ScanLine, XCircle } from "lucide-react";
import { Notice } from "@centro-cirurgico/ui";

import { useOnline } from "../hooks/useOnline";
import { errorMessage } from "../utils/api";
import type { ScanFeedback, ScanTone } from "./scan-actions";

export const WRISTBAND_FORMATS = ["qr_code", "code_128", "code_39", "ean_13", "itf", "data_matrix"] as const;
export type WristbandFormat = (typeof WRISTBAND_FORMATS)[number];

const OFFLINE_MESSAGE = "Sem conexão. A leitura não será registrada.";
const DEFAULT_FEEDBACK_MS = 4000;
const NATIVE_DETECT_INTERVAL_MS = 180;
const DUPLICATE_WINDOW_MS = 1500;
const MIN_CODE_LENGTH = 2;

export interface WristbandScannerHandle {
  /** Retoma a leitura imediatamente (ex.: diálogo cancelado). */
  reset: () => void;
  /** Mostra um resultado (com vibração) e retoma após o tempo padrão. */
  showFeedback: (feedback: ScanFeedback) => void;
  focus: () => void;
}

export interface WristbandScannerProps {
  /**
   * Chamado a cada leitura. Retorne o feedback a exibir, ou `null` para manter o leitor pausado
   * (a tela abrirá um diálogo e depois chamará `reset()` ou `showFeedback()`).
   */
  onScan: (code: string, format?: string) => Promise<ScanFeedback | null> | ScanFeedback | null;
  idleTitle?: string | undefined;
  idleDetail?: string | undefined;
  /** Pausa externa (diálogo aberto, requisição em andamento). */
  hold?: boolean | undefined;
  /** Desliga a leitura por completo (ex.: sala bloqueada). */
  disabled?: boolean | undefined;
  disabledReason?: string | undefined;
  /** Usa a câmera (padrão: sim). O leitor físico e a digitação continuam disponíveis. */
  camera?: boolean | undefined;
  feedbackMs?: number | undefined;
  manualLabel?: string | undefined;
  manualButtonLabel?: string | undefined;
}

type CameraState = "off" | "starting" | "active" | "unavailable";
type Engine = "native" | "zxing";

function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type;
    return !["button", "checkbox", "radio", "submit", "reset", "range", "color", "file"].includes(type);
  }
  return false;
}

function cameraErrorText(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Câmera bloqueada pelo navegador. Use o leitor ou digite o código.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Nenhuma câmera encontrada. Use o leitor ou digite o código.";
    case "NotReadableError":
    case "AbortError":
      return "A câmera está em uso por outro aplicativo.";
    case "NotSupportedError":
      return "A câmera exige HTTPS. Use o leitor ou digite o código.";
    default:
      return "Câmera indisponível. Use o leitor ou digite o código.";
  }
}

function vibrate(tone: ScanTone) {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
  const pattern = tone === "ok" ? [90] : tone === "warn" ? [60, 60, 60] : tone === "error" ? [200, 80, 200] : [40];
  try {
    navigator.vibrate(pattern);
  } catch {
    // Alguns navegadores lançam quando a página não teve interação; ignorar.
  }
}

function ToneIcon({ tone }: { tone: ScanTone }) {
  const props = { "aria-hidden": true as const, size: 64 };
  if (tone === "ok") return <CheckCircle2 {...props} />;
  if (tone === "warn") return <AlertTriangle {...props} />;
  if (tone === "error") return <XCircle {...props} />;
  return <Info {...props} />;
}

async function createZxingReader(video: HTMLVideoElement, onResult: (text: string, format: string) => void) {
  const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
    import("@zxing/browser"),
    import("@zxing/library")
  ]);
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.QR_CODE,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.EAN_13,
    BarcodeFormat.ITF,
    BarcodeFormat.DATA_MATRIX
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 200 });
  return reader.decodeFromVideoElement(video, (result) => {
    if (!result) return;
    onResult(result.getText(), BarcodeFormat[result.getBarcodeFormat()]?.toLowerCase() ?? "unknown");
  });
}

export const WristbandScanner = forwardRef<WristbandScannerHandle, WristbandScannerProps>(function WristbandScanner(
  {
    onScan,
    idleTitle = "Pronto para ler",
    idleDetail = "Aproxime a pulseira da câmera ou use o leitor.",
    hold = false,
    disabled = false,
    disabledReason,
    camera = true,
    feedbackMs = DEFAULT_FEEDBACK_MS,
    manualLabel = "Código da pulseira",
    manualButtonLabel = "Confirmar código"
  },
  ref
) {
  const online = useOnline();
  const [feedback, setFeedback] = useState<ScanFeedback | null>(null);
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [cameraState, setCameraState] = useState<CameraState>("off");
  const [cameraMessage, setCameraMessage] = useState<string | null>(null);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [manualCode, setManualCode] = useState("");
  const [cameraAttempt, setCameraAttempt] = useState(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wedgeRef = useRef<HTMLInputElement | null>(null);
  const feedbackTimerRef = useRef<number | null>(null);
  const lastReadRef = useRef<{ code: string; at: number } | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // Bloqueio "duro" (nunca pode ser contornado) e bloqueio de leitura (inclui a pausa pós-resultado).
  const hardBlocked = !online || disabled || hold || busy;
  const readingBlocked = hardBlocked || paused;
  const hardBlockedRef = useRef(hardBlocked);
  hardBlockedRef.current = hardBlocked;
  const readingBlockedRef = useRef(readingBlocked);
  readingBlockedRef.current = readingBlocked;

  const clearFeedbackTimer = useCallback(() => {
    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  const resume = useCallback(() => {
    clearFeedbackTimer();
    setFeedback(null);
    setPaused(false);
    lastReadRef.current = null;
  }, [clearFeedbackTimer]);

  const showFeedback = useCallback(
    (next: ScanFeedback) => {
      clearFeedbackTimer();
      setFeedback(next);
      setPaused(true);
      vibrate(next.tone);
      feedbackTimerRef.current = window.setTimeout(() => {
        feedbackTimerRef.current = null;
        setFeedback(null);
        setPaused(false);
      }, feedbackMs);
    },
    [clearFeedbackTimer, feedbackMs]
  );

  const focusWedge = useCallback(() => {
    const input = wedgeRef.current;
    if (!input) return;
    if (isTextField(document.activeElement) && document.activeElement !== input) return;
    input.focus({ preventScroll: true });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: resume,
      showFeedback,
      focus: focusWedge
    }),
    [resume, showFeedback, focusWedge]
  );

  const handleRead = useCallback(
    async (rawCode: string, format?: string, options: { force?: boolean } = {}) => {
      const code = rawCode.trim();
      if (code.length < MIN_CODE_LENGTH) return;
      // Entrada manual/teclado (`force`) pode substituir um resultado ainda na tela,
      // mas nunca contorna offline, desabilitado, diálogo aberto ou requisição em andamento.
      if (options.force ? hardBlockedRef.current : readingBlockedRef.current) return;
      const now = Date.now();
      const last = lastReadRef.current;
      if (!options.force && last && last.code === code && now - last.at < DUPLICATE_WINDOW_MS) return;
      lastReadRef.current = { code, at: now };

      // Pausa imediatamente: nenhuma nova leitura até o resultado aparecer.
      clearFeedbackTimer();
      setFeedback(null);
      setPaused(true);
      setBusy(true);
      hardBlockedRef.current = true;
      readingBlockedRef.current = true;
      try {
        const result = await (format ? onScanRef.current(code, format) : onScanRef.current(code));
        if (result) showFeedback(result);
        // `null`: a tela abriu um diálogo e chamará reset()/showFeedback() depois.
      } catch (error) {
        showFeedback({ tone: "error", title: errorMessage(error) });
      } finally {
        setBusy(false);
      }
    },
    [clearFeedbackTimer, showFeedback]
  );

  // Limpeza do temporizador ao desmontar.
  useEffect(() => clearFeedbackTimer, [clearFeedbackTimer]);

  // Câmera: liga quando desejada e possível; desliga ao desmontar ou quando desabilitada.
  const cameraWanted = camera && online && !disabled;
  useEffect(() => {
    if (!cameraWanted) {
      setCameraState("off");
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setCameraState("unavailable");
      setCameraMessage(cameraErrorText(new DOMException("HTTPS required", "NotSupportedError")));
      return;
    }

    setCameraState("starting");
    setCameraMessage(null);
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      })
      .then(async (stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        await video.play();
        if (cancelled) return;
        setEngine("BarcodeDetector" in window ? "native" : "zxing");
        setCameraState("active");
        stream.getVideoTracks().forEach((track) =>
          track.addEventListener("ended", () => {
            if (streamRef.current !== stream) return;
            setCameraState("unavailable");
            setCameraMessage("A câmera foi desconectada. Use o leitor ou digite o código.");
          })
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setCameraState("unavailable");
        setCameraMessage(cameraErrorText(error));
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      video.pause();
      video.srcObject = null;
      setEngine(null);
    };
  }, [cameraWanted, cameraAttempt]);

  // Laço de detecção: só roda com a câmera ativa e sem bloqueio de leitura.
  const detecting = cameraState === "active" && !readingBlocked;
  useEffect(() => {
    if (!detecting || !engine) return;
    const video = videoRef.current;
    if (!video) return;
    let stopped = false;

    if (engine === "native") {
      let detector: BarcodeDetector;
      try {
        detector = new BarcodeDetector({ formats: [...WRISTBAND_FORMATS] });
      } catch {
        setEngine("zxing");
        return;
      }
      let inFlight = false;
      const interval = window.setInterval(() => {
        if (stopped || inFlight || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
        inFlight = true;
        detector
          .detect(video)
          .then((codes) => {
            if (stopped) return;
            const first = codes.find((item) => item.rawValue && item.rawValue.trim().length >= MIN_CODE_LENGTH);
            if (first) void handleRead(first.rawValue, first.format);
          })
          .catch(() => {
            // Quadro inválido: tenta no próximo intervalo.
          })
          .finally(() => {
            inFlight = false;
          });
      }, NATIVE_DETECT_INTERVAL_MS);
      return () => {
        stopped = true;
        window.clearInterval(interval);
      };
    }

    let controls: { stop: () => void } | null = null;
    createZxingReader(video, (text, format) => {
      if (stopped) return;
      void handleRead(text, format);
    })
      .then((scanControls) => {
        if (stopped) {
          scanControls.stop();
          return;
        }
        controls = scanControls;
      })
      .catch(() => {
        if (stopped) return;
        setCameraState("unavailable");
        setCameraMessage("Não foi possível iniciar o leitor da câmera. Use o leitor físico ou digite o código.");
      });
    return () => {
      stopped = true;
      controls?.stop();
    };
  }, [detecting, engine, handleRead]);

  // Leitor físico (teclado): mantém o campo oculto focado quando nenhum campo de texto está em uso.
  useEffect(() => {
    if (disabled) return;
    focusWedge();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.target === wedgeRef.current) return;
      if (isTextField(event.target)) return;
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) focusWedge();
    };
    const onFocus = () => focusWedge();
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("focus", onFocus);
    };
  }, [disabled, focusWedge]);

  function handleWedgeKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter" && event.key !== "Tab") return;
    const input = event.currentTarget;
    const value = input.value;
    input.value = "";
    if (event.key === "Enter") event.preventDefault();
    if (value.trim().length >= MIN_CODE_LENGTH) void handleRead(value, "keyboard", { force: true });
  }

  function handleManualSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const code = manualCode.trim();
    if (code.length < MIN_CODE_LENGTH) return;
    setManualCode("");
    void handleRead(code, "manual", { force: true });
  }

  const canRead = !readingBlocked;
  const statusText = !online
    ? OFFLINE_MESSAGE
    : disabled
      ? (disabledReason ?? "Leitura desativada.")
      : busy
        ? "Registrando leitura…"
        : hold
          ? "Aguardando confirmação…"
          : idleTitle;

  return (
    <div className={`wristband-scanner${canRead ? " ready" : ""}`} data-engine={engine ?? undefined}>
      {!online && <Notice error={OFFLINE_MESSAGE} />}
      {online && disabled && disabledReason && <Notice warning={disabledReason} />}

      {camera && (
        <div className={`scan-camera ${cameraState}${paused ? " paused" : ""}`}>
          <video ref={videoRef} className="scan-video" autoPlay playsInline muted aria-label="Câmera do leitor" />
          <div className="scan-frame" aria-hidden="true" />
          {cameraState === "active" && canRead && <div className="scan-band" aria-hidden="true" />}
          {cameraState !== "active" && (
            <div className="scan-camera-message" role="status">
              {cameraState === "starting" ? (
                <>
                  <Camera aria-hidden="true" size={22} /> Iniciando câmera…
                </>
              ) : cameraState === "unavailable" ? (
                <>
                  <CameraOff aria-hidden="true" size={22} />
                  <span>{cameraMessage}</span>
                  <button
                    type="button"
                    className="secondary compact"
                    onClick={() => setCameraAttempt((attempt) => attempt + 1)}
                  >
                    Tentar novamente
                  </button>
                </>
              ) : (
                <>
                  <CameraOff aria-hidden="true" size={22} /> Câmera desligada
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="scan-result-slot" aria-live="assertive" aria-atomic="true">
        {feedback ? (
          <div className={`scan-result ${feedback.tone === "info" ? "" : feedback.tone}`.trim()} role="status">
            <ToneIcon tone={feedback.tone} />
            <strong>{feedback.title}</strong>
            {feedback.detail && <p>{feedback.detail}</p>}
            {feedback.warning && <p className="scan-warning">{feedback.warning}</p>}
          </div>
        ) : (
          <div className={`scan-result scan-idle${canRead ? "" : " muted"}`} role="status">
            <ScanLine aria-hidden="true" size={64} />
            <strong>{statusText}</strong>
            {canRead && <p>{idleDetail}</p>}
          </div>
        )}
      </div>

      <label className="sr-only" htmlFor="wristband-wedge">
        Leitor de código de barras (teclado)
      </label>
      <input
        id="wristband-wedge"
        ref={wedgeRef}
        className="scan-wedge"
        type="text"
        inputMode="none"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        tabIndex={-1}
        disabled={disabled}
        onKeyDown={handleWedgeKeyDown}
        aria-describedby="wristband-wedge-hint"
      />
      <p id="wristband-wedge-hint" className="scan-hint">
        <Keyboard aria-hidden="true" size={16} /> Leitores USB/Bluetooth funcionam automaticamente.
      </p>

      <form className="scan-manual" onSubmit={handleManualSubmit}>
        <label htmlFor="wristband-manual">
          {manualLabel}
          <input
            id="wristband-manual"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            value={manualCode}
            onChange={(event) => setManualCode(event.target.value)}
            placeholder="Digite o código impresso"
            disabled={disabled || !online}
          />
        </label>
        <button
          type="submit"
          className="secondary"
          disabled={disabled || !online || busy || manualCode.trim().length < MIN_CODE_LENGTH}
        >
          {manualButtonLabel}
        </button>
      </form>
    </div>
  );
});
